import * as React from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  GetLocationsDocument,
  LocationWithEmployeesDocument,
  LocationsEmployeesCollectionListDocument,
  UpdateEmployeeWithLocationDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

interface Props {
  locationId: number
  onClose: () => void
}

const AddEmployee = ({ onClose, locationId }: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  const apolloClient = useApolloClient()
  const { loading, data } = useQuery(LocationWithEmployeesDocument, {
    variables: { ids: [locationId] },
    fetchPolicy: 'network-only',
  })
  const [selectedEmployees, setSelectedEmployees] = React.useState<
    EmployeeForSelectFragmentNoAccess[]
  >([])

  const location = data?.locations?.locationsConnection?.nodes?.find(
    (loc: { id: number }) => loc.id === locationId
  )

  const currentEmployeesIds: number[] =
    location?.employees?.map((employee: { id: number }) => employee.id) || []

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!location) throw new Error('Unknown location')

    // FIXME: It can be super interesting to convert this promise all to a bulk action
    const promises = selectedEmployees.map(({ id }) => {
      return apolloClient.mutate({
        mutation: UpdateEmployeeWithLocationDocument,
        variables: {
          employeeId: id,
          locationId: location.id,
        },
      })
    })

    const { success } = await asyncAction(() => Promise.all(promises))

    if (success) {
      apolloClient.refetchQueries({
        include: [
          LocationWithEmployeesDocument,
          GetLocationsDocument,
          LocationsEmployeesCollectionListDocument,
        ],
      })

      closePortal()
    } else {
      // Workaround for a more descriptive error until product handles the issue with the manager permissions
      app.setFlash({
        type: 'wrong',
        message: i18n.t('settings.locations.location.employees.add.error_permissions'),
        time: 3000,
      })
    }
  }

  if (loading || !location) return null

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      <Modal
        illustration="user-male-female"
        title={i18n.t('settings.locations.location.employees.add.title', {
          name: location.name,
        })}
        description={i18n.t('settings.locations.location.employees.add.description')}
        onClose={onClose}
        onSubmit={onSubmit}
        disableFocusTrap
        footer={
          <Button disabled={!selectedEmployees.length} submit>
            {i18n.t('settings.locations.location.employees.add.submit')}
          </Button>
        }
      >
        <Box maxWidth="s400">
          <EmployeeSelector
            excludeIds={currentEmployeesIds}
            multiple
            value={selectedEmployees}
            withAvatar
            excludeCurrentEmployee
            onSelect={(employees) => setSelectedEmployees(employees)}
          />
        </Box>
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(AddEmployee)
